import {Box, BoxProps, Chip, Typography, useTheme} from "@mui/material"
import React, {FC} from "react"
import {makeStyles} from "tss-react/mui";
import type {ILesson, IModuleScheduled} from "@buildwitt/shapeshift-types";
import type {ICourseModule} from "@buildwitt/shapeshift-types/ICourse";
import {CheckBox, Quiz, LockOutlined} from "@mui/icons-material";
import LockIcon from "@mui/icons-material/Lock";
import {Link} from "react-router-dom";
import {palette} from "@/constants";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {VideoPreview} from "@/app2/pages/TrainingFeedPage/VideoPreview";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import type {TodayLearningModuleDto} from "@/app2/pages/HomePage/types";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import {formatDueInstantInTimezone, getDateInTimezone} from "@/app2/shared/utils/dateManipulation";


interface IProps extends BoxProps {
	module: IModuleScheduled | TodayLearningModuleDto
	/** When set, due labels and overdue styling use this IANA zone (learner profile / browser). */
	timeZone?: string
}

const formatDuration = (durationInSeconds: number): string => {
	// Handle invalid/missing duration values
	if (!durationInSeconds || isNaN(durationInSeconds) || durationInSeconds < 0) {
		return "00:00";
	}
	return new Date(durationInSeconds * 1000).toISOString().slice(14, 19);
};

export const isNotOverdue = (dueDate: Date | null, timeZone?: string): boolean => {
	if (!dueDate) return true;
	if (timeZone) {
		const dueY = getDateInTimezone(new Date(dueDate), timeZone);
		const todayY = getDateInTimezone(new Date(), timeZone);
		return dueY >= todayY;
	}
	const due = new Date(dueDate);
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	due.setHours(0, 0, 0, 0);
	return due >= today;
};

export const getVideoThumbnail = (module: ICourseModule | TodayLearningModuleDto | null): string | undefined => {
	if (!module) return undefined;

	// Check if it's TodayLearningModuleDto
	const isTodayLearningModule = (mod: any): mod is TodayLearningModuleDto => {
		return "courseSlug" in mod && "courseTitle" in mod;
	};

	if (isTodayLearningModule(module)) {
		return module.videoThumbnailUrl || "/holding_image.jpg";
	}

	const schedModule = module as IModuleScheduled;
	if (schedModule.moduleThumbnailOverride)
		return schedModule.moduleThumbnailOverride.formats?.small?.url ?? schedModule.moduleThumbnailOverride.url;

	if (module?.type === "lesson") {
		const lesson = module as ILesson;

		if (lesson.videoThumbnailUrl) {
			return lesson.videoThumbnailUrl;
		}

		return "/holding_image.jpg"
	}

	return undefined;
};

const useStyles = makeStyles<{complete: boolean; isLocked: boolean, isOverdue: boolean}>()((theme, {complete, isLocked, isOverdue}) => ({
	itemContent: {
		width: "300px",
		height: "386px",
		flex: "0 0 auto",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		boxShadow: theme.shadows[2],
		margin: 4,
		position: "relative",
		cursor: isLocked ? "not-allowed" : "pointer",
		transition: "all 0.3s ease",
		borderRadius: "24px",
		borderWidth: "1px",
		borderStyle: "solid",
		background: "#141414",
		opacity: isLocked ? 0.6 : 1,
		"&:hover": {
			borderColor: isLocked ? "transparent" : isOverdue ? theme.palette.error.main : theme.palette.primary.main,
			transform: isLocked ? "none" : "translateY(-4px)",
			boxShadow: isLocked ? theme.shadows[2] : theme.shadows[4],
			"& .hover-orange-overlay": {
				opacity: isLocked ? 0 : 1
			}
		},
		"[class*='previewImageSvg']": {
			width: "32px!important",
			height: "32px",
			svg: {
				width: "44px",
				height: "44px",
				transform: "translate(-6px, -6px)",
				marginRight: "auto",
				marginLeft: "auto"
			}
		}
	},
	title: {
		marginBottom: "0",
		overflow: "hidden",
		textOverflow: "ellipsis",
		display: "-webkit-box",
		WebkitLineClamp: "2",
		WebkitBoxOrient: "vertical"
	},
	chip: {
		fontWeight: 600
	},
	blankPreview: {
		width: "calc(100% + 60px)",
		height: "220px",
		top: "0",
		position: "relative",
		left: "-30px",
		overflow: "hidden",
		backgroundColor: theme.palette.primary.main,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		svg: {
			fill: theme.palette.common.white,
			width: "38px",
			height: "38px"
		}
	},
	videoThumbnailStyle: {
		backgroundColor: "transparent",
		borderRadius: "16px",
		overflow: "hidden",
		position: "relative",
		margin: "16px",
		border: "none",
		width: "100%",
		lineHeight: "1em",
		height: "220px",
		":hover": {
			backgroundColor: "transparent",
			border: "none"
		}
	},
	cardContents: {
		display: "flex",
		flexFlow: "wrap",
		height: "calc(100% - 220px)",
		padding: "0px 16px",
		boxSizing: "border-box"
	},
	itemDetailsContainer: {
		margin: "5px 0 15px",
		border: "none",
		flexGrow: 3,
		display: "flex",
		gap: "10px",
		flexDirection: "column",
		"& h3": {
			[theme.breakpoints.down("sm")]: {
				fontSize: "19px"
			}
		}

	},
	itemLink: {
		display: "flex",
		flexFlow: "wrap",
		height: "100%"
	},
	durationChip: {
		color: palette.white,
		backgroundColor: palette.grey16,
		"& .MuiChip-label": {
			fontSize: "11px"
		}
	},
	statusChip: {
		color:  complete ? palette.grey100 : palette.white,
		backgroundColor: complete ? palette.success : palette.error,
		"& .MuiChip-label": {
			fontSize: "11px"
		}
	},
	lockedOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 10,
		pointerEvents: "none"
	},
	lockedPreviewIcon: {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: "75px",
		height: "75px",
		color: palette.orange,
		zIndex: 11,
		pointerEvents: "none",
		filter: "drop-shadow(0 2px 6px rgba(0, 0, 0, 0.5))",
	},
	enrollmentTypeWithLock: {
		display: "flex",
		alignItems: "center",
		gap: "4px"
	}
}));


export function ModuleDuration(props: {
	durationInSeconds: number,
	complete: boolean,
	dueDate: Date | null,
	timeZone?: string
}) {
	const {t} = useSSTranslation();
	const theme = useTheme();
	const {classes} = useStyles({complete: props.complete, isLocked: false, isOverdue: false});
	const dueDateLabel = props.dueDate
		? (props.timeZone
			? formatDueInstantInTimezone(props.dueDate, props.timeZone)
			: new Date(props.dueDate).toLocaleDateString())
		: "";
	return <Box gap={1} display="flex">
		<Chip
			className={classes.durationChip}
			label={formatDuration(props.durationInSeconds)}
			size="small"
			icon={<PlayArrowOutlined style={{color: theme.palette.action.active}}/>}
		/>
		{props.dueDate && (
			props.complete ? (
				<Chip
					icon={<CheckCircleOutline style={{color: palette.grey100}}/>}
					className={classes.statusChip}
					label={`${t("common_completed", "Completed")} ${dueDateLabel}`}
					size="small"
				/>
			) : !isNotOverdue(props.dueDate, props.timeZone) ? (
				<Chip
					icon={<CalendarTodayOutlined style={{color: palette.white, fontSize: 15}}/>}
					className={classes.statusChip}
					label={`${t("common_due", "Due")} ${dueDateLabel}`}
					size="small"
				/>
			) : (
				<Chip
					icon={<CalendarTodayOutlined style={{color: theme.palette.action.active, fontSize: 15}}/>}
					className={classes.durationChip}
					label={`${t("common_due", "Due")} ${dueDateLabel}`}
					size="small"
				/>
			)
		)}
	</Box>;
}

export const TodayLearningCard:FC<IProps> = ({className, module, timeZone}) => {
	// Type guard to check if module is TodayLearningModuleDto
	const isTodayLearningModule = (mod: any): mod is TodayLearningModuleDto => {
		return "courseSlug" in mod && "courseTitle" in mod;
	};

	// Check if module has isLocked property (from new API)
	const isLocked = isTodayLearningModule(module)
		? module.isLocked
		: (module as any).isLocked ?? false;

	const dueDate = module.dueDate 
		? (typeof module.dueDate === "string" ? new Date(module.dueDate as string) : module.dueDate as Date)
		: null;

	const {classes, cx} = useStyles({complete: module.complete, isLocked, isOverdue: dueDate ? !isNotOverdue(dueDate, timeZone) : false});
	const {t} = useSSTranslation();
	const theme = useTheme();

	// Helper to get course data based on module type
	const getCourseData = () => {
		if (isTodayLearningModule(module)) {
			return {
				slug: module.courseSlug,
				title: module.courseTitle
			};
		}
		return {
			slug: (module as IModuleScheduled).course.slug,
			title: (module as IModuleScheduled).course.title
		};
	};

	const getVideoUrl = (module: ICourseModule | TodayLearningModuleDto | null): string | undefined => {
		if (!module) return undefined;

		if (isTodayLearningModule(module)) {
			return module.videoUrl || undefined;
		}

		if (module?.type === "lesson") {
			return (module as ILesson).videoUrl
		}
		return undefined;
	}

	const courseData = getCourseData();
	const videoUrl = getVideoUrl(module);
	const videoThumbnail = getVideoThumbnail(module);
	const moduleDuration = module.duration || 0;

	// Wrapper component to conditionally render Link or Box based on locked status
	const CardWrapper = ({children}: { children: React.ReactNode }) => {
		if (isLocked) {
			return <Box className={classes.itemLink}>{children}</Box>;
		}
		return (
			<Link
				className={classes.itemLink}
				to={"/feed/" + courseData.slug + "/" + module.type + "/" + module.slug}
				data-test-id="training-feed-module-link"
				aria-label={`Open ${module.type} ${module.title} from ${courseData.title}`}
			>
				{children}
			</Link>
		);
	};

	return (
		<Box
			borderColor={module.complete ? theme.palette.success.main : (dueDate && !isNotOverdue(dueDate, timeZone)) ? theme.palette.error.main : "rgba(161, 161, 160, 0.15)"}
			className={cx(classes.itemContent, className)}
			data-test-id="training-feed-module-card"
			role="article"
			aria-label={`Training module: ${module.title}${isLocked ? " (Locked)" : ""}`}
		>
			{isLocked && <Box className={classes.lockedOverlay} />}
			<CardWrapper>
				<Box
					className={classes.videoThumbnailStyle}
					data-test-id="training-feed-module-preview"
					aria-label={`${module.type} preview`}
				>
					{(videoUrl && videoThumbnail)
						? <VideoPreview videoUrl={videoThumbnail} disablePlayIcon={true} />
						: <Box className={classes.blankPreview} aria-label={`${module.type} icon`}>
							{module.type === "survey" ? <CheckBox/> : <Quiz />}
						</Box>
					}
					{isLocked && <LockIcon className={classes.lockedPreviewIcon} aria-hidden />}
				</Box>
				<Box className={classes.cardContents}>
					<Box className={classes.itemDetailsContainer}>
						<ModuleDuration durationInSeconds={moduleDuration} complete={module.complete} dueDate={dueDate} timeZone={timeZone} />
						<Typography
							className={classes.title}
							variant="h4"
							data-test-id="training-feed-module-title"
							aria-level={4}
						>
							{module.title}
						</Typography>
						{(module.courseEnrollmentType) && (
							<Box className={classes.enrollmentTypeWithLock}>
								{isLocked && (
									<LockOutlined
										sx={{
											fontSize: 16,
											color: palette.grey70
										}}
									/>
								)}
								<Typography
									variant="body2"
									component="div"
									align="left"
									color={palette.grey70}
								>
									{module.courseEnrollmentType && t(`user_enrollment_type_${module.courseEnrollmentType}` as any, module.courseEnrollmentType)}
								</Typography>
							</Box>
						)}
					</Box>
				</Box>
			</CardWrapper>
		</Box>
	)
}